import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Pencil, Trash2, Phone, CreditCard, IdCard } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PhotoCapture } from "../components/PhotoCapture";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { nombre: "", cedula: "", licencia: "", telefono: "" };

export default function Conductores() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [foto, setFoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });

  const openNew = () => { setEditing(null); setForm(EMPTY); setFoto(null); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ nombre: d.nombre, cedula: d.cedula, licencia: d.licencia, telefono: d.telefono }); setFoto(d.foto || null); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/drivers/${editing.id}`, { ...form, foto });
      else await api.post("/drivers", { ...form, foto });
      toast.success(editing ? "Conductor actualizado" : "Conductor registrado");
      qc.invalidateQueries({ queryKey: ["drivers"] });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`¿Eliminar al conductor ${d.nombre}?`)) return;
    await api.delete(`/drivers/${d.id}`);
    toast.success("Conductor eliminado");
    qc.invalidateQueries({ queryKey: ["drivers"] });
  };

  return (
    <div className="space-y-5" data-testid="conductores-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-600" /> Conductores
          </h1>
          <p className="text-sm text-slate-500">Registro de conductores vinculados a movimientos</p>
        </div>
        <Button onClick={openNew} className="bg-navy hover:bg-navy-800 text-white" data-testid="new-driver-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Conductor
        </Button>
      </div>

      {drivers.length === 0 && (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400 text-sm" data-testid="drivers-empty">No hay conductores registrados</CardContent></Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {drivers.map((d) => (
          <Card key={d.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`driver-card-${d.cedula}`}>
            <CardContent className="p-4 flex gap-4">
              <div className="w-16 h-16 rounded-xl bg-navy overflow-hidden flex items-center justify-center shrink-0">
                {d.foto ? <img src={d.foto} alt={d.nombre} className="w-full h-full object-cover" /> : <Users className="w-6 h-6 text-slate-500" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-outfit font-bold text-navy truncate">{d.nombre}</p>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(d)} className="p-1.5 text-slate-400 hover:text-cyan-600" data-testid={`edit-driver-${d.cedula}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(d)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`delete-driver-${d.cedula}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><IdCard className="w-3 h-3" /> {d.cedula}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Lic. {d.licencia}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {d.telefono}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="driver-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editing ? "Editar Conductor" : "Registrar Conductor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="nombre">Nombre completo</Label>
              <Input id="nombre" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                     placeholder="Juan Pérez" data-testid="driver-nombre-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cedula">Cédula</Label>
                <Input id="cedula" required value={form.cedula} onChange={(e) => setForm({ ...form, cedula: e.target.value })}
                       placeholder="001-0000000-0" data-testid="driver-cedula-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="licencia">Licencia</Label>
                <Input id="licencia" required value={form.licencia} onChange={(e) => setForm({ ...form, licencia: e.target.value })}
                       placeholder="L-123456" data-testid="driver-licencia-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="telefono">Teléfono</Label>
              <Input id="telefono" required value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                     placeholder="+1 809 000 0000" data-testid="driver-telefono-input" />
            </div>
            <PhotoCapture label="Foto del conductor" value={foto} onChange={setFoto} testid="driver-photo" />
            <Button type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy-800 text-white h-11" data-testid="driver-save-button">
              {saving ? "Guardando..." : editing ? "Guardar Cambios" : "Registrar Conductor"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
